// ============================================================================
// ADD-ON REQUEST SERVICE
// ============================================================================

import { get, post, put, patch, del } from '../client/axiosClient';
import { addonRequestEndpoints } from '../endpoints/addonRequest.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import { transformPaginatedResponse } from '../types/common.types';
import type {
  AddonRequest,
  CreateAddonRequestPayload,
  UpdateAddonRequestPayload,
  AddonRequestFilterParams,
} from '../types/addonRequest.types';

class AddonRequestService {
  async getAll(params?: AddonRequestFilterParams): Promise<PaginatedResponse<AddonRequest>> {
    const endpoint = params
      ? buildEndpointWithQuery(addonRequestEndpoints.list(), params)
      : addonRequestEndpoints.list();
    const response = await get(endpoint);
    return transformPaginatedResponse<AddonRequest>(response.data);
  }

  async getById(id: ID): Promise<ApiResponse<AddonRequest>> {
    const response = await get<ApiResponse<AddonRequest>>(addonRequestEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateAddonRequestPayload): Promise<ApiResponse<AddonRequest>> {
    const response = await post<ApiResponse<AddonRequest>>(addonRequestEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateAddonRequestPayload): Promise<ApiResponse<AddonRequest>> {
    const response = await put<ApiResponse<AddonRequest>>(addonRequestEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(addonRequestEndpoints.delete(id));
    return response.data;
  }

  async submit(id: ID): Promise<ApiResponse<AddonRequest>> {
    const response = await patch<ApiResponse<AddonRequest>>(addonRequestEndpoints.submit(id), {});
    return response.data;
  }

  async checkDuplicateLoanAccount(loanAccountNumber: string, excludeId?: string): Promise<boolean> {
    const response = await get<ApiResponse<{ exists: boolean }>>(
      addonRequestEndpoints.checkDuplicateLoanAccount(loanAccountNumber, excludeId)
    );
    return response.data.data?.exists ?? false;
  }
}

export const addonRequestService = new AddonRequestService();
export default addonRequestService;
